// Local, self-contained design models. The untouched supplied contract is in reference/.
// Restore the real server types before production integration; do not substitute these for authorization.
export type DemoRole = "owner" | "operator" | "viewer";
export type Capability = "operate" | "configure" | "admin";
export type Tone = "green" | "amber" | "red" | "blue" | "neutral";
export type Call = {
  id: string;
  time: string;
  from: string;
  name: string | null;
  account: string;
  reason: string;
  outcome: "Informed" | "Message taken" | "Escalated" | "Abandoned";
  seconds: number;
  flagged: boolean;
  transcript: { who: "caller" | "assistant"; text: string }[];
  recordingUrl: string | null;
};
export type Escalation = {
  id: string;
  callId: string;
  caller: string;
  summary: string;
  state: "notifying" | "acked" | "resolved";
  target: string;
  time: string;
  severity: "Urgent" | "Normal";
  note: string;
};
export type Proposal = {
  id: string;
  kind: string;
  caller: string;
  rationale: string;
  effect: string;
  status: "pending" | "approved" | "rejected";
  callId: string;
};
export type InboxItem = {
  id: string;
  kind: "Message" | "Ticket" | "Lead";
  subject: string;
  body: string;
  name: string;
  number: string;
  time: string;
  resolved: boolean;
};
export type FlowStep = {
  id: string;
  type:
    | "entry"
    | "say"
    | "ask"
    | "lookup"
    | "branch"
    | "message"
    | "page"
    | "end";
  text: string;
  x: number;
  y: number;
};
export type Flow = {
  id: string;
  name: string;
  description: string;
  kind: "posture" | "action";
  icon: string;
  pinned: boolean;
  armed: boolean;
  armedUntil: string | null;
  stale: boolean;
  retired?: boolean;
  version: number;
  steps: FlowStep[];
  edges: { id: string; source: string; target: string }[];
};
export type Policy = {
  ticketWrite: "proposal" | "live" | "local";
  restoreSms: "proposal" | "allowed" | "off";
  officeHours: string;
  portal: string;
  emergencyKeywords: string[];
  ladder: { target: string; channel: string; afterMinutes: number }[];
};
export type DemoState = {
  stormArmed: boolean;
  additionalOutages: Array<DemoState["outage"] & { id: string }>;
  numbers: { id: string; e164: string; label: string; active: boolean }[];
  decisionRules: Record<string, "always" | "never">;
  outage: {
    status: "confirmed" | "restoring" | "resolved";
    label: string;
    scope: string;
    affectedCount: number;
    bannerText: string;
    etaText: string;
    etaSource: string;
    waiting: number;
  };
  calls: Call[];
  escalations: Escalation[];
  proposals: Proposal[];
  inbox: InboxItem[];
  flows: Flow[];
  policy: Policy;
  voice: {
    id: string;
    stability: number;
    likeness: number;
    pace: number;
    script: string;
  };
  integrations: {
    id: string;
    name: string;
    kind: string;
    status: "connected" | "unverified" | "disconnected";
    detail: string;
  }[];
  users: {
    id: string;
    name: string;
    email: string;
    role: DemoRole;
    disabled: boolean;
  }[];
  brand: {
    name: string;
    greeting: string;
    emailDigest: boolean;
    smsAlerts: boolean;
  };
};
export function can(role: DemoRole, capability: Capability = "operate") {
  return role === "owner" || (role === "operator" && capability === "operate");
}
export function money(cents: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}
export function duration(seconds: number) {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}
export function setStorm(state: DemoState, armed: boolean) {
  state.stormArmed = armed;
  const flow = state.flows.find((f) => f.id === "storm");
  if (flow) flow.armed = armed;
}
export function activationIssue(flow: Flow, callId?: string): string | null {
  if (flow.retired) return "This flow has been retired.";
  if (flow.stale) return "Update this flow before activating it.";
  if (flow.kind === "action" && !callId)
    return "Choose a call before running this action.";
  return null;
}
export function activeOutages(state: DemoState) {
  return [state.outage, ...state.additionalOutages].filter(
    (o) => o.status !== "resolved",
  );
}
export function announcementConflicts(state: DemoState) {
  return Boolean(
    state.outage.etaText.trim() &&
      /no (restoration|estimated|restore)|don.t have a restoration|no estimate/i.test(
        state.outage.bannerText,
      ),
  );
}
